/*
  Lablana Atelier — theme behaviour.
  Deliberately small: no framework, no polyfills, no third-party libraries.
  Everything degrades to a usable state when JS is unavailable.
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- *
     Horizontal scrollers (reviews, carousels)
     Buttons carry aria-controls pointing at the scrollable track.
   * ---------------------------------------------------------------- */

  function initScrollers(root) {
    root.querySelectorAll('[data-lab-scroll]').forEach(function (button) {
      if (button.dataset.labScrollBound) return;
      button.dataset.labScrollBound = 'true';

      button.addEventListener('click', function () {
        var track = document.getElementById(button.getAttribute('aria-controls'));
        if (!track) return;

        var first = track.firstElementChild;
        var step = first ? first.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
        var direction = button.dataset.labScroll === 'next' ? 1 : -1;

        track.scrollBy({
          left: step * direction,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      });
    });
  }

  /* ---------------------------------------------------------------- *
     Reveal on scroll
   * ---------------------------------------------------------------- */

  function initReveal(root) {
    var items = root.querySelectorAll('.lab-reveal:not(.is-in)');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    items.forEach(function (el) { observer.observe(el); });
  }

  /* ---------------------------------------------------------------- *
     Sticky add to cart
     Shown only once the real add-to-cart button has scrolled out of view,
     so it never competes with the primary control. Mirrors the live
     variant id, price and availability from the main product form.
   * ---------------------------------------------------------------- */

  function initStickyAtc(root) {
    var sticky = root.querySelector('[data-lab-sticky-atc]');
    if (!sticky || sticky.dataset.labBound) return;
    sticky.dataset.labBound = 'true';

    var form = document.getElementById(sticky.dataset.formId);
    var anchor = document.querySelector('[data-lab-atc-anchor]');
    if (!form || !anchor) return;

    var stickyButton = sticky.querySelector('[data-lab-sticky-submit]');

    // Submitting the real form keeps every existing cart listener, analytics
    // event and app integration intact.
    if (stickyButton) {
      stickyButton.addEventListener('click', function (event) {
        event.preventDefault();
        var realButton = form.querySelector('[type="submit"]');
        if (realButton) realButton.click();
      });
    }

    // A rAF-throttled scroll listener rather than IntersectionObserver: the
    // observer only reports at threshold crossings, and its cached rect gave
    // the wrong answer on tall mobile pages where a jump-scroll skipped the
    // crossing. Measuring the anchor directly is deterministic at any
    // scroll position, including after a reload restores one.
    var ticking = false;

    function update() {
      ticking = false;
      var rect = anchor.getBoundingClientRect();
      // Show once the real button is fully above the viewport, and hide again
      // near the very bottom so it never sits on top of the footer CTA.
      var scrolledPast = rect.bottom < 0;
      var atPageEnd =
        window.innerHeight + window.scrollY >= document.body.scrollHeight - 120;
      sticky.classList.toggle('is-visible', scrolledPast && !atPageEnd);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* Dawn refreshes only the ids it knows about (price, sku, inventory…), so
     our own variant-dependent blocks are swapped in from the same section HTML
     Dawn has already fetched. No extra network request. */

  function refreshVariantAwareBlocks(data) {
    if (!data || !data.html) return;
    ['LabStock'].forEach(function (prefix) {
      var source = data.html.getElementById(prefix + '-' + data.sectionId);
      var destination = document.querySelector('[id^="' + prefix + '-"][data-lab-variant-refresh]');
      if (source && destination) destination.innerHTML = source.innerHTML;
    });
  }

  /* Keep the sticky bar in step with variant changes.

     Dawn re-renders the product section on every variant change and publishes
     `variant-change` with the freshly rendered HTML. Reading price and
     availability out of that HTML means the sticky bar always shows exactly
     what the main buy box shows — correct money formatting, correct locale,
     correct sold-out state — without duplicating any of that logic here. */

  function syncStickyOnVariantChange(root) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    if (document.documentElement.dataset.labVariantBound) return;
    document.documentElement.dataset.labVariantBound = 'true';

    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var data = event && event.data;
      if (!data) return;

      refreshVariantAwareBlocks(data);

      var sticky = document.querySelector('[data-lab-sticky-atc]');
      if (!sticky) return;

      var variant = data.variant;
      var html = data.html;

      var label = sticky.querySelector('[data-lab-sticky-variant]');
      if (label) label.textContent = variant ? variant.title : '';

      if (html) {
        var freshPrice = html.getElementById('price-' + data.sectionId);
        var target = sticky.querySelector('.lab-sticky-atc__price');
        if (freshPrice && target) target.innerHTML = freshPrice.innerHTML;
      }

      var button = sticky.querySelector('[data-lab-sticky-submit]');
      if (button) {
        var unavailable = !variant || !variant.available;
        button.disabled = unavailable;
        var text = button.querySelector('[data-lab-sticky-label]');
        if (text) {
          text.textContent = unavailable
            ? (window.variantStrings && window.variantStrings.soldOut) || text.textContent
            : (window.variantStrings && window.variantStrings.addToCart) || text.textContent;
        }
      }

      var image = sticky.querySelector('.lab-sticky-atc__image img');
      if (image && variant && variant.featured_media && variant.featured_media.preview_image) {
        var src = variant.featured_media.preview_image.src;
        image.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=160';
        image.srcset = '';
      }
    });
  }

  /* ---------------------------------------------------------------- *
     Boot
   * ---------------------------------------------------------------- */

  /* Auto-rotating announcement bar must be pausable (WCAG 2.2.2). Dawn's
     slideshow-component exposes pause()/play(); hover and keyboard focus
     both stop the rotation, leaving it resumes it. */
  function initAnnouncementPause(root) {
    root.querySelectorAll('.announcement-bar slideshow-component').forEach(function (el) {
      if (el.dataset.labPauseBound) return;
      el.dataset.labPauseBound = 'true';
      var stop = function () { if (typeof el.pause === 'function') el.pause(); };
      var go = function () { if (typeof el.play === 'function') el.play(); };
      el.addEventListener('mouseenter', stop);
      el.addEventListener('mouseleave', go);
      el.addEventListener('focusin', stop);
      el.addEventListener('focusout', go);
    });
  }

  /* Size-guide link under the size picker: open the matching accordion and
     scroll to it, instead of a dead jump to a closed <details>. */
  function initSizeLink(root) {
    root.querySelectorAll('[data-lab-open-tab]').forEach(function (a) {
      if (a.dataset.labBound) return; a.dataset.labBound = 'true';
      a.addEventListener('click', function (e) {
        var id = a.getAttribute('href').slice(1);
        var details = document.getElementById(id) || document.querySelector('details[id*="tab_size"]');
        if (!details) return;
        e.preventDefault();
        details.open = true;
        details.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        var summary = details.querySelector('summary'); if (summary) summary.focus();
      });
    });
  }

  /* Bottom nav: search opens Dawn's search modal; cart opens the drawer. */
  function initBottomNav(root) {
    var search = root.querySelector('[data-lab-open-search]');
    if (search && !search.dataset.labBound) {
      search.dataset.labBound = 'true';
      search.addEventListener('click', function () {
        var summary = document.querySelector('details-modal .header__icon--search, .header__search summary');
        if (summary) { summary.click(); return; }
        window.location.href = '/search';
      });
    }
    var cart = root.querySelector('[data-lab-open-cart]');
    if (cart && !cart.dataset.labBound) {
      cart.dataset.labBound = 'true';
      cart.addEventListener('click', function (e) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') { e.preventDefault(); drawer.open(); }
      });
    }
  }

  function init(root) {
    initScrollers(root);
    initBottomNav(root);
    initSizeLink(root);
    initAnnouncementPause(root);
    initReveal(root);
    initStickyAtc(root);
    syncStickyOnVariantChange(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();

/* ------------------------------------------------------------------ *
   Bundle tiers ("Kup więcej, zapłać mniej"), selected-value labels,
   price inside the add-to-cart button.
 * ------------------------------------------------------------------ */
(function () {
  function money(cents) {
    var v = (cents / 100).toFixed(2).replace('.', ',');
    var parts = v.split(','); parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join(',') + ' zł';
  }
  function syncLabels() {
    document.querySelectorAll('[data-lab-option]').forEach(function (fs) {
      var checked = fs.querySelector('input:checked'); var out = fs.querySelector('[data-lab-selected]');
      if (checked && out) out.textContent = checked.value;
    });
  }
  function init() {
    var root = document.querySelector('[data-lab-bundle]');
    if (!root || root.dataset.labBound) return;
    root.dataset.labBound = 'true';
    var data = JSON.parse(root.querySelector('[data-lab-bundle-data]').textContent);
    var unit = parseInt(root.dataset.unit, 10) || 0;
    var form = document.getElementById('product-form-' + root.dataset.section);
    var submit = form && form.querySelector('.product-form__submit');
    var priceSlot = submit && submit.querySelector('[data-lab-atc-price]');
    var cards = [].slice.call(root.querySelectorAll('[data-lab-tier]'));

    function tier() { var c = root.querySelector('.lab-bundle__radio:checked'); return c ? parseInt(c.value, 10) : 1; }
    function pctFor(t) { var c = root.querySelector('[data-lab-tier="' + t + '"]'); return c ? parseInt(c.dataset.pct, 10) : 0; }
    function paint() {
      cards.forEach(function (c) {
        var t = parseInt(c.dataset.labTier, 10), p = parseInt(c.dataset.pct, 10);
        var was = unit * t, now = Math.round(was * (100 - p) / 100);
        c.querySelector('[data-lab-now]').textContent = money(now);
        var w = c.querySelector('[data-lab-was]'); if (w) w.textContent = money(was);
        c.classList.toggle('is-selected', t === tier());
      });
      if (priceSlot) {
        var t = tier(), p = pctFor(t), was = unit * t, now = Math.round(was * (100 - p) / 100);
        priceSlot.innerHTML = (p ? '<s>' + money(was) + '</s> ' : '') + '<b>' + money(now) + '</b>';
      }
    }
    function syncRows(variant) {
      if (!variant) return;
      root.querySelectorAll('[data-lab-row]').forEach(function (row) {
        row.querySelectorAll('[data-lab-opt]').forEach(function (sel) {
          var i = parseInt(sel.dataset.labOpt, 10);
          if (variant.options && variant.options[i] != null) sel.value = variant.options[i];
        });
      });
    }
    root.addEventListener('change', function (e) { if (e.target.classList.contains('lab-bundle__radio')) paint(); });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var v = event && event.data && event.data.variant;
        if (v) { unit = v.price; syncRows(v); paint(); }
        syncLabels();
      });
    }
    document.querySelectorAll('[data-lab-option]').forEach(function (fs) { fs.addEventListener('change', syncLabels); });
    if (form) {
      form.addEventListener('submit', function (e) {
        var t = tier(); if (t < 2) return;
        e.preventDefault(); e.stopImmediatePropagation();
        var err = root.querySelector('[data-lab-bundle-error]'); err.hidden = true;
        var card = root.querySelector('[data-lab-tier="' + t + '"]');
        var items = [];
        var ok = [].every.call(card.querySelectorAll('[data-lab-row]'), function (row) {
          var vals = [].map.call(row.querySelectorAll('[data-lab-opt]'), function (s) { return s.value; });
          var v = data.variants.find(function (x) { return x.options.join('||') === vals.join('||'); });
          if (!v || !v.available) return false;
          var ex = items.find(function (i) { return i.id === v.id; });
          if (ex) ex.quantity += 1; else items.push({ id: v.id, quantity: 1 });
          return true;
        });
        if (!ok) { err.hidden = false; return; }
        submit.classList.add('loading'); submit.setAttribute('aria-disabled', 'true');
        var drawer = document.querySelector('cart-drawer');
        var sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map(function (s) { return s.id; }) : ['cart-drawer', 'cart-icon-bubble'];
        fetch(window.routes.cart_add_url, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ items: items, sections: sections, sections_url: window.location.pathname })
        }).then(function (r) { return r.json(); }).then(function (res) {
          submit.classList.remove('loading'); submit.removeAttribute('aria-disabled');
          if (res.status) { err.textContent = res.description || res.message; err.hidden = false; return; }
          if (drawer && drawer.renderContents) { drawer.classList.remove('is-empty'); drawer.renderContents(res); }
          else window.location.href = window.routes.cart_url;
        }).catch(function () { submit.classList.remove('loading'); submit.removeAttribute('aria-disabled'); });
      }, true);
    }
    paint();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();

/* ------------------------------------------------------------------ *
   Shipping protection toggle in the cart drawer / cart page.
   Adds or removes the add-on variant, then re-renders the cart sections.
 * ------------------------------------------------------------------ */
(function () {
  var busy = false;
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input.classList || !input.classList.contains('lab-protect__input')) return;
    var box = input.closest('[data-lab-protect]');
    if (!box || busy) return;
    busy = true;
    box.classList.add('is-loading');
    var drawer = document.querySelector('cart-drawer');
    var sections = drawer && drawer.getSectionsToRender
      ? drawer.getSectionsToRender().map(function (s) { return s.id; })
      : ['cart-drawer', 'cart-icon-bubble'];
    var url, body;
    if (input.checked) {
      url = window.routes.cart_add_url;
      body = { items: [{ id: parseInt(box.dataset.variant, 10), quantity: 1 }], sections: sections, sections_url: window.location.pathname };
    } else {
      url = window.routes.cart_change_url;
      body = { id: box.dataset.key, quantity: 0, sections: sections, sections_url: window.location.pathname };
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); }).then(function (res) {
      busy = false;
      if (res.status) { input.checked = !input.checked; box.classList.remove('is-loading'); return; }
      if (drawer && drawer.renderContents && res.sections) { drawer.renderContents(res); }
      else { window.location.reload(); }
    }).catch(function () { busy = false; input.checked = !input.checked; box.classList.remove('is-loading'); });
  });
})();

/* Offer timer: countdown to the end of the current cycle in local time, then restarts.
   Same element on the bar and in the tiles, so both always show the same clock. */
(function () {
  'use strict';
  function pad(n) { return String(n).padStart(2, '0'); }
  function tick() {
    var els = document.querySelectorAll('[data-lab-timer]');
    if (!els.length) return;
    var now = new Date();
    var dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    var elapsed = now.getTime() - dayStart;
    els.forEach(function (el) {
      var cycle = (Number(el.dataset.labTimer) || 24) * 36e5;
      var left = cycle - (elapsed % cycle);
      el.textContent = pad(Math.floor(left / 36e5)) + ':' + pad(Math.floor(left / 6e4) % 60) + ':' + pad(Math.floor(left / 1e3) % 60);
    });
  }
  tick();
  setInterval(tick, 1000);
  window.labTimerTick = tick;
})();

